import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import Layout from '@/components/Layout';
import { toast } from 'sonner';
import { Upload, Users, Search, Building2, Mail, MapPin, Trash2, Plus, Loader2, Send, Printer } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useNavigate, useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Suppliers({ token, user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const supplierRefs = useRef({});
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLetter, setSelectedLetter] = useState('all');
  const [showOutstanding, setShowOutstanding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [supplierToEdit, setSupplierToEdit] = useState(null);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  
  const [newSupplier, setNewSupplier] = useState({
    company_name: '',
    street: '',
    city: '',
    zipcode: '',
    country: '',
    email: '',
    contact_person: '',
    logo_data: null
  });

  const [editSupplier, setEditSupplier] = useState({
    company_name: '',
    street: '',
    city: '',
    zipcode: '',
    country: '',
    email: '',
    contact_person: '',
    logo_data: null,
    send_email_on_delivery: true,
    requires_paper_delivery_note: false
  });

  const [newSupplierLogoPreview, setNewSupplierLogoPreview] = useState(null);
  const [editSupplierLogoPreview, setEditSupplierLogoPreview] = useState(null);

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  useEffect(() => {
    fetchSuppliers();
  }, []);

  // Filter to show only one supplier when returning from detail view
  useEffect(() => {
    if (location.state?.scrollToSupplierId && suppliers.length > 0) {
      const supplierId = location.state.scrollToSupplierId;
      const supplier = suppliers.find(c => c.id === supplierId);
      
      if (supplier) {
        // Set search term to show only this supplier
        setSearchTerm(supplier.company_name);
        setSelectedLetter('all');
        
        // Scroll to top to see the single card
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }
      
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location.state, suppliers]);

  useEffect(() => {
    let filtered = suppliers;

    // Filter by outstanding packaging
    if (showOutstanding) {
      filtered = filtered.filter(supplier =>
        (supplier.palette_balance || 0) > 0 ||
        (supplier.eurobox_balance || 0) > 0 ||
        (supplier.aufsatzrahmen_balance || 0) > 0
      );
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(supplier =>
        supplier.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (supplier.city && supplier.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (supplier.email && supplier.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (supplier.contact_person && supplier.contact_person.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Filter by alphabet
    if (selectedLetter !== 'all') {
      filtered = filtered.filter(supplier => 
        supplier.company_name.toUpperCase().startsWith(selectedLetter)
      );
    }
    
    setFilteredSuppliers(filtered);
  }, [searchTerm, selectedLetter, suppliers, showOutstanding]);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
      setFilteredSuppliers(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Lieferantn');
    }
  };

  const handleXMLUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/suppliers/import-xml`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(response.data.message);
      setUploadDialogOpen(false);
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim XML-Import');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/suppliers`, newSupplier, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lieferant erfolgreich angelegt');
      setCreateDialogOpen(false);
      setNewSupplier({
        company_name: '',
        street: '',
        city: '',
        zipcode: '',
        country: '',
        email: '',
        contact_person: '',
        logo_data: null
      });
      setNewSupplierLogoPreview(null);
      fetchSuppliers();
    } catch (error) {
      // Handle Pydantic validation errors
      const detail = error.response?.data?.detail;
      let errorMessage = 'Fehler beim Anlegen des Lieferantn';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewSupplierLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log('📤 Uploading supplier logo:', file.name, file.type, `${(file.size / 1024).toFixed(2)} KB`);

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo-Datei ist zu groß. Maximale Größe: 5MB');
      return;
    }

    // Validate image file types
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!validImageTypes.includes(file.type)) {
      console.error('❌ Invalid file type:', file.type);
      toast.error(`Ungültiger Dateityp: ${file.type}. Nur JPG, PNG, GIF, SVG, WebP sind erlaubt.`);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      try {
        const base64String = reader.result;
        console.log('✅ Supplier logo loaded successfully:', file.name, `${(base64String.length / 1024).toFixed(2)} KB base64`);
        setNewSupplier({ ...newSupplier, logo_data: base64String });
        setNewSupplierLogoPreview(base64String);
        toast.success(`Logo "${file.name}" erfolgreich geladen`);
      } catch (error) {
        console.error('❌ Error processing supplier logo:', error);
        toast.error('Fehler beim Verarbeiten der Datei');
      }
    };
    reader.onerror = (error) => {
      console.error('❌ FileReader error:', error);
      toast.error('Fehler beim Laden der Datei');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveNewSupplierLogo = () => {
    setNewSupplier({ ...newSupplier, logo_data: null });
    setNewSupplierLogoPreview(null);
  };

  const handleEditSupplier = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.put(`${API}/suppliers/${supplierToEdit.id}`, editSupplier, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Lieferant erfolgreich aktualisiert');
      setEditDialogOpen(false);
      setSupplierToEdit(null);
      setEditSupplierLogoPreview(null);
      fetchSuppliers();
    } catch (error) {
      // Handle Pydantic validation errors
      const detail = error.response?.data?.detail;
      let errorMessage = 'Fehler beim Aktualisieren';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditSupplierLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log('📤 Uploading supplier logo:', file.name, file.type, `${(file.size / 1024).toFixed(2)} KB`);

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo-Datei ist zu groß. Maximale Größe: 5MB');
      return;
    }

    // Validate image file types
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!validImageTypes.includes(file.type)) {
      console.error('❌ Invalid file type:', file.type);
      toast.error(`Ungültiger Dateityp: ${file.type}. Nur JPG, PNG, GIF, SVG, WebP sind erlaubt.`);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      try {
        const base64String = reader.result;
        console.log('✅ Supplier logo loaded successfully:', file.name, `${(base64String.length / 1024).toFixed(2)} KB base64`);
        setEditSupplier({ ...editSupplier, logo_data: base64String });
        setEditSupplierLogoPreview(base64String);
        toast.success(`Logo "${file.name}" erfolgreich geladen`);
      } catch (error) {
        console.error('❌ Error processing supplier logo:', error);
        toast.error('Fehler beim Verarbeiten der Datei');
      }
    };
    reader.onerror = (error) => {
      console.error('❌ FileReader error:', error);
      toast.error('Fehler beim Laden der Datei');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveEditSupplierLogo = () => {
    setEditSupplier({ ...editSupplier, logo_data: null });
    setEditSupplierLogoPreview(null);
  };

  const handleOpenEditDialog = (supplier) => {
    setSupplierToEdit(supplier);
    setEditSupplier({
      company_name: supplier.company_name,
      street: supplier.street || '',
      city: supplier.city || '',
      zipcode: supplier.zipcode || '',
      country: supplier.country || '',
      email: supplier.email || '',
      contact_person: supplier.contact_person || '',
      logo_data: supplier.logo_data || null,
      send_email_on_delivery: supplier.send_email_on_delivery !== false,
      requires_paper_delivery_note: supplier.requires_paper_delivery_note === true
    });
    setEditSupplierLogoPreview(supplier.logo_data || null);
    setEditDialogOpen(true);
  };

  const handleDeleteSupplier = async () => {
    if (!supplierToDelete) return;

    try {
      await axios.delete(`${API}/suppliers/${supplierToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lieferant erfolgreich gelöscht');
      setDeleteDialogOpen(false);
      setSupplierToDelete(null);
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen des Lieferantn');
    }
  };

  const handleToggleSelectSupplier = (supplierId) => {
    setSelectedSuppliers(prev =>
      prev.includes(supplierId)
        ? prev.filter(id => id !== supplierId)
        : [...prev, supplierId]
    );
  };

  const handleSelectAll = () => {
    if (selectedSuppliers.length === filteredSuppliers.length) {
      setSelectedSuppliers([]);
    } else {
      setSelectedSuppliers(filteredSuppliers.map(c => c.id));
    }
  };

  const handleBulkDelete = async () => {
    try {
      setIsLoading(true);
      
      // Delete all selected suppliers
      for (const supplierId of selectedSuppliers) {
        await axios.delete(`${API}/suppliers/${supplierId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success(`${selectedSuppliers.length} Lieferant(n) erfolgreich gelöscht`);
      setBulkDeleteDialogOpen(false);
      setSelectedSuppliers([]);
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCleanupEmpty = async () => {
    try {
      const response = await axios.post(`${API}/suppliers/cleanup-empty`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchSuppliers();
    } catch (error) {
      toast.error('Fehler beim Bereinigen');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="supplier-list">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#0A1F33' }}>Lieferantn</h1>
            <p className="text-gray-600 mt-1">Lieferantndatenbank verwalten und XML importieren</p>
          </div>
          <div className="flex space-x-2">
            <Button
              variant={showOutstanding ? "default" : "outline"}
              onClick={() => setShowOutstanding(!showOutstanding)}
              className={showOutstanding ? "bg-amber-600 hover:bg-amber-700" : ""}
            >
              📦 Ausstehend
              {showOutstanding && ` (${filteredSuppliers.length})`}
            </Button>
            {selectedSuppliers.length > 0 && (
              <Button
                variant="outline"
                onClick={() => setBulkDeleteDialogOpen(true)}
                className="border-red-300 text-red-600 hover:bg-red-50"
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {selectedSuppliers.length} ausgewählt löschen
              </Button>
            )}
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="metaxon-primary text-white" data-testid="create-supplier-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Lieferant anlegen
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="create-supplier-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Neuen Lieferanten anlegen</DialogTitle>
                  <DialogDescription>
                    Geben Sie die Lieferantendaten manuell ein
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateSupplier} className="space-y-3 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="company-name">Firmenname *</Label>
                    <Input
                      id="company-name"
                      data-testid="company-name-input"
                      value={newSupplier.company_name}
                      onChange={(e) => setNewSupplier({ ...newSupplier, company_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="street">Straße</Label>
                    <Input
                      id="street"
                      data-testid="street-input"
                      value={newSupplier.street}
                      onChange={(e) => setNewSupplier({ ...newSupplier, street: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="zipcode">PLZ</Label>
                      <Input
                        id="zipcode"
                        data-testid="zipcode-input"
                        value={newSupplier.zipcode}
                        onChange={(e) => setNewSupplier({ ...newSupplier, zipcode: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">Stadt</Label>
                      <Input
                        id="city"
                        data-testid="city-input"
                        value={newSupplier.city}
                        onChange={(e) => setNewSupplier({ ...newSupplier, city: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Land</Label>
                    <Input
                      id="country"
                      data-testid="country-input"
                      value={newSupplier.country}
                      onChange={(e) => setNewSupplier({ ...newSupplier, country: e.target.value })}
                      placeholder="z.B. D, CH, AT"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="email-input"
                      value={newSupplier.email}
                      onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-person">Ansprechpartner</Label>
                    <Input
                      id="contact-person"
                      data-testid="contact-person-input"
                      value={newSupplier.contact_person}
                      onChange={(e) => setNewSupplier({ ...newSupplier, contact_person: e.target.value })}
                      placeholder="Name des Ansprechpartners"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="supplier-logo">Lieferantn-Logo (optional)</Label>
                    <Input
                      id="supplier-logo"
                      type="file"
                      accept="image/*"
                      onChange={handleNewSupplierLogoUpload}
                      data-testid="supplier-logo-input"
                    />
                    <p className="text-xs text-gray-500">Alle Bildformate (JPG, PNG, SVG, WebP, GIF, etc.), max. 5MB</p>
                    {newSupplierLogoPreview && (
                      <div className="mt-2 flex items-center gap-3">
                        <img
                          src={newSupplierLogoPreview}
                          alt="Logo Vorschau"
                          className="h-16 w-16 object-contain border rounded"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={handleRemoveNewSupplierLogo}
                        >
                          Logo entfernen
                        </Button>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                      Abbrechen
                    </Button>
                    <Button type="submit" className="metaxon-primary text-white" disabled={isLoading} data-testid="create-supplier-submit-btn">
                      {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird erstellt...</> : 'Lieferant anlegen'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button className="metaxon-primary text-white" data-testid="import-xml-btn">
                  <Upload className="mr-2 h-4 w-4" />
                  XML Importieren
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="import-xml-dialog">
                <DialogHeader>
                  <DialogTitle>Lieferantn aus XML importieren</DialogTitle>
                  <DialogDescription>
                    Laden Sie eine XML-Datei hoch, um Lieferantndaten zu importieren
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="xml-file">XML-Datei auswählen</Label>
                    <Input
                      id="xml-file"
                      type="file"
                      accept=".xml"
                      onChange={handleXMLUpload}
                      disabled={isLoading}
                      data-testid="xml-file-input"
                    />
                  </div>
                  {isLoading && (
                    <div className="text-sm text-gray-600">Importiere Lieferantn...</div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            
            <Button 
              variant="outline" 
              onClick={handleCleanupEmpty}
              data-testid="cleanup-btn"
            >
              Leere bereinigen
            </Button>
          </div>
        </div>

        <Card className="glass-effect border-0">
          <CardHeader>
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Search className="h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Lieferantn suchen nach Firmenname, Stadt, E-Mail oder Ansprechpartner..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="supplier-search-input"
                />
              </div>
              
              {/* Alphabet Filter */}
              <div className="flex flex-wrap gap-1">
                <Button
                  variant={selectedLetter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedLetter('all')}
                  className={selectedLetter === 'all' ? 'metaxon-primary text-white' : ''}
                  data-testid="filter-all"
                >
                  Alle
                </Button>
                {alphabet.map(letter => (
                  <Button
                    key={letter}
                    variant={selectedLetter === letter ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedLetter(letter)}
                    className={selectedLetter === letter ? 'metaxon-primary text-white' : 'min-w-[40px]'}
                    data-testid={`filter-${letter}`}
                  >
                    {letter}
                  </Button>
                ))}
              </div>
              
              {/* Select All Checkbox */}
              {filteredSuppliers.length > 0 && (
                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox
                    id="select-all"
                    checked={selectedSuppliers.length === filteredSuppliers.length}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                  <Label htmlFor="select-all" className="text-sm font-medium">
                    Alle auswählen ({filteredSuppliers.length})
                  </Label>
                </div>
              )}
            </div>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSuppliers.length === 0 ? (
            <Card className="col-span-full glass-effect border-0">
              <CardContent className="pt-6 text-center">
                <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">
                  {searchTerm ? 'Keine Lieferantn gefunden' : 'Noch keine Lieferantn vorhanden. Importieren Sie eine XML-Datei oder legen Sie manuell Lieferantn an.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredSuppliers.map((supplier) => (
              <Card 
                key={supplier.id}
                ref={el => supplierRefs.current[supplier.id] = el}
                className="glass-effect border-0 hover:shadow-lg transition-shadow cursor-pointer relative overflow-hidden" 
                data-testid={`supplier-card-${supplier.id}`}
                onClick={() => navigate(`/suppliers/${supplier.id}`)}
                style={supplier.logo_data ? {
                  backgroundImage: `url(${supplier.logo_data})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundBlendMode: 'overlay'
                } : {}}
              >
                {/* Semi-transparent overlay when logo exists */}
                {supplier.logo_data && (
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: `url(${supplier.logo_data})`,
                      backgroundSize: 'contain',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      opacity: 0.08,
                      zIndex: 0
                    }}
                  />
                )}
                <CardHeader className="relative z-10">
                  <div className="flex items-start justify-between space-x-3">
                    <div className="flex items-start space-x-3 flex-1 min-w-0">
                      <Checkbox
                        checked={selectedSuppliers.includes(supplier.id)}
                        onCheckedChange={() => handleToggleSelectSupplier(supplier.id)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`supplier-checkbox-${supplier.id}`}
                      />
                      <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600">
                        <Building2 className="h-5 w-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg truncate" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                          {supplier.company_name}
                        </CardTitle>
                        {supplier.supplier_id && (
                          <CardDescription className="text-xs">ID: {supplier.supplier_id}</CardDescription>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSupplierToDelete(supplier);
                        setDeleteDialogOpen(true);
                      }}
                      data-testid={`delete-supplier-btn-${supplier.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm relative z-10">
                  {supplier.contact_person && (
                    <div className="flex items-center space-x-2 font-medium text-blue-700">
                      <Users className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{supplier.contact_person}</span>
                    </div>
                  )}
                  {supplier.street && (
                    <div className="flex items-start space-x-2">
                      <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <div>{supplier.street}</div>
                        <div>
                          {supplier.zipcode} {supplier.city}
                        </div>
                        {supplier.country && <div className="text-gray-500">{supplier.country}</div>}
                      </div>
                    </div>
                  )}
                  {supplier.email && (
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate text-blue-600">{supplier.email}</span>
                    </div>
                  )}
                  {((supplier.palette_balance || 0) > 0 || (supplier.eurobox_balance || 0) > 0 || (supplier.aufsatzrahmen_balance || 0) > 0) && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                      <strong className="text-amber-900">Verpackungsmaterial:</strong>
                      <div className="flex gap-3 mt-1">
                        {(supplier.palette_balance || 0) > 0 && (
                          <span className="text-amber-800">📦 {supplier.palette_balance} Paletten</span>
                        )}
                        {(supplier.eurobox_balance || 0) > 0 && (
                          <span className="text-amber-800">📦 {supplier.eurobox_balance} Euroboxen</span>
                        )}
                        {(supplier.aufsatzrahmen_balance || 0) > 0 && (
                          <span className="text-amber-800">📦 {supplier.aufsatzrahmen_balance} Aufsatzrahmen</span>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="pt-2 flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEditDialog(supplier);
                      }}
                      data-testid={`edit-supplier-btn-${supplier.id}`}
                    >
                      Ändern
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {filteredSuppliers.length > 0 && (
          <div className="text-center text-sm text-gray-600">
            {filteredSuppliers.length} von {suppliers.length} Lieferantn angezeigt
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent data-testid="edit-supplier-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Lieferant ändern</DialogTitle>
            <DialogDescription>
              Aktualisieren Sie die Lieferantendaten
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSupplier} className="space-y-3 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-company-name">Firmenname *</Label>
              <Input
                id="edit-company-name"
                data-testid="edit-company-name-input"
                value={editSupplier.company_name}
                onChange={(e) => setEditSupplier({ ...editSupplier, company_name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-street">Straße</Label>
              <Input
                id="edit-street"
                data-testid="edit-street-input"
                value={editSupplier.street}
                onChange={(e) => setEditSupplier({ ...editSupplier, street: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-zipcode">PLZ</Label>
                <Input
                  id="edit-zipcode"
                  data-testid="edit-zipcode-input"
                  value={editSupplier.zipcode}
                  onChange={(e) => setEditSupplier({ ...editSupplier, zipcode: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-city">Stadt</Label>
                <Input
                  id="edit-city"
                  data-testid="edit-city-input"
                  value={editSupplier.city}
                  onChange={(e) => setEditSupplier({ ...editSupplier, city: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-country">Land</Label>
              <Input
                id="edit-country"
                data-testid="edit-country-input"
                value={editSupplier.country}
                onChange={(e) => setEditSupplier({ ...editSupplier, country: e.target.value })}
                placeholder="z.B. D, CH, AT"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">E-Mail</Label>
              <Input
                id="edit-email"
                type="email"
                data-testid="edit-email-input"
                value={editSupplier.email}
                onChange={(e) => setEditSupplier({ ...editSupplier, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-contact-person">Ansprechpartner</Label>
              <Input
                id="edit-contact-person"
                data-testid="edit-contact-person-input"
                value={editSupplier.contact_person}
                onChange={(e) => setEditSupplier({ ...editSupplier, contact_person: e.target.value })}
                placeholder="Name des Ansprechpartners"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-supplier-logo">Lieferantn-Logo (optional)</Label>
              <Input
                id="edit-supplier-logo"
                type="file"
                accept="image/*"
                onChange={handleEditSupplierLogoUpload}
                data-testid="edit-supplier-logo-input"
              />
              <p className="text-xs text-gray-500">Alle Bildformate (JPG, PNG, SVG, WebP, GIF, etc.), max. 5MB</p>
              {editSupplierLogoPreview && (
                <div className="mt-2 flex items-center gap-3">
                  <img
                    src={editSupplierLogoPreview}
                    alt="Logo Vorschau"
                    className="h-16 w-16 object-contain border rounded"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={handleRemoveEditSupplierLogo}
                  >
                    Logo entfernen
                  </Button>
                </div>
              )}
            </div>

            {/* Lieferschein-Einstellungen */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-semibold text-gray-900">📄 Lieferschein-Einstellungen</h4>
              
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-start space-x-3 flex-1">
                  <Send className="h-4 w-4 text-blue-500 mt-1" />
                  <div>
                    <Label className="text-sm font-medium">E-Mail-Versand nach Unterschrift</Label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Lieferschein automatisch per E-Mail verschicken
                    </p>
                  </div>
                </div>
                <Switch
                  checked={editSupplier.send_email_on_delivery}
                  onCheckedChange={(checked) => 
                    setEditSupplier({ ...editSupplier, send_email_on_delivery: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-start space-x-3 flex-1">
                  <Printer className="h-4 w-4 text-orange-500 mt-1" />
                  <div>
                    <Label className="text-sm font-medium">Papier-Lieferschein erforderlich</Label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Lieferant benötigt gedruckten Lieferschein
                    </p>
                  </div>
                </div>
                <Switch
                  checked={editSupplier.requires_paper_delivery_note}
                  onCheckedChange={(checked) => 
                    setEditSupplier({ ...editSupplier, requires_paper_delivery_note: checked })
                  }
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" className="metaxon-primary text-white" disabled={isLoading} data-testid="edit-supplier-submit-btn">
                {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird gespeichert...</> : 'Änderungen speichern'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-supplier-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Lieferant wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den Lieferantn "{supplierToDelete?.company_name}" wirklich löschen? 
              Diese Aktion kann nicht rückgängig gemacht werden.
              {supplierToDelete && (
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-800 font-medium">
                    ⚠️ Hinweis: Lieferantn mit Lieferscheinen können nicht gelöscht werden.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSupplier}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-supplier-btn"
            >
              Lieferant löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Ausgewählte Lieferantn löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie {selectedSuppliers.length} ausgewählte Lieferantn wirklich löschen? 
              Diese Aktion kann nicht rückgängig gemacht werden.
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800 font-medium">
                  ⚠️ Hinweis: Lieferantn mit Lieferscheinen können nicht gelöscht werden.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-bulk-delete-btn">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-bulk-delete-btn"
              disabled={isLoading}
            >
              {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird gelöscht...</> : `${selectedSuppliers.length} Lieferantn löschen`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
